const initialState = () => {
  return {
    status: 'DRAFT',
    fontConfig: {},
    colorConfig: {
      primary: '#FFFFFF',
      secondary: '#FFFFFF',
    },
    template: '',
    description: '',
    fees: '',
    socialNetworks: [],
    content: [],
    site: '',
    legalNotice: '',
    invisible: false,
    onLine: true,
    onLineUpdated: false,
  };
};

const getters = {
  getState: state => {
    // Ensure content is sent as ARRAY to API
    // Site-generator's transform() expects array to convert to {'Intro-title': 'value', ...}
    let contentArray;
    if (Array.isArray(state.content)) {
      contentArray = state.content;
    } else if (typeof state.content === 'object' && state.content !== null) {
      // Convert object {"0": {...}, "1": {...}} to array [{...}, {...}]
      contentArray = Object.values(state.content).filter(
        item => item && typeof item === 'object' && item.id
      );
    } else {
      contentArray = [];
    }
    
    return {
      ...state,
      content: contentArray,
    };
  },

  getSocialNetworkUrlByType: state => type => {
    const social = state.socialNetworks.filter(social => social.type === type)[0];
    return social ? social.url : '';
  },

  getSocialNetworks: state => state.socialNetworks,

  getDescription: state => state.description,

  getFees: state => state.fees,

  getColorConfig: state => state.colorConfig,

  getFontConfig: state => state.fontConfig,

  getSite: state => state.site,

  getTemplate: state => state.template,

  getContent: state => {
    // Return content as array for iteration (LiveEdit needs .forEach())
    // Content should always be an array after our mutation normalizes it
    if (Array.isArray(state.content)) {
      return state.content;
    } else if (typeof state.content === 'object' && state.content !== null) {
      // If somehow still an object, convert to array
      return Object.values(state.content).filter(
        item => item && typeof item === 'object' && item.id
      );
    }
    return [];
  },

  getStatus: state => state.status,

  getLegalNotice: state => state.legalNotice,

  isInvisible: state => state.invisible,

  isOnLine: state => !state.onLine,

  isOnLineUpdated: state => !state.onLineUpdated,
};

const mutations = {
  setSite(state, site) {
    state.site = site;
  },

  setTemplate(state, template) {
    state.template = template;
  },

  addSocialNetwork(state, socialNetwork) {
    state.socialNetworks.push(socialNetwork);
  },

  removeSocialNetwork(state, socialNetwork) {
    state.socialNetworks = state.socialNetworks.filter(
      social => social.type !== socialNetwork.type
    );
  },

  modifySocialNetwork(state, socialNetwork) {
    state.socialNetworks = [
      ...state.socialNetworks.filter(social => social.type !== socialNetwork.type),
      socialNetwork,
    ];
  },

  setDescription(state, description) {
    state.description = description;
  },

  setFees(state, fees) {
    state.fees = fees;
  },

  setColorConfig(state, colorConfig) {
    state.colorConfig = colorConfig;
  },

  setFontConfig(state, fontConfig) {
    state.fontConfig = fontConfig;
  },

  setContent(state, content) {
    state.content = content;
  },

  setSocialNetworks(state, socialNetworks) {
    state.socialNetworks = socialNetworks;
  },

  setContentElementById(state, element) {
    // Ensure we only store serializable properties (no DOM elements)
    const cleanElement = {
      id: element.id,
      type: element.type,
      content: element.content,
    };
    
    if (Array.isArray(state.content)) {
      // If content is an array, update the matching element
      const index = state.content.findIndex(el => el.id === element.id);
      if (index !== -1) {
        state.content = [
          ...state.content.slice(0, index),
          cleanElement,
          ...state.content.slice(index + 1),
        ];
      } else {
        state.content = [...state.content, cleanElement];
      }
    } else if (typeof state.content === 'object' && state.content !== null) {
      // Content is an object with numeric keys like {"0": {...}, "1": {...}}
      // Find the key that contains the element with matching id and update it
      const existingKey = Object.keys(state.content).find(
        key => state.content[key]?.id === element.id
      );
      
      if (existingKey !== undefined) {
        // Update existing entry, preserving the object structure
        state.content = {
          ...state.content,
          [existingKey]: cleanElement,
        };
      } else {
        // Add new entry with next numeric key
        const numericKeys = Object.keys(state.content).filter(k => !isNaN(k)).map(Number);
        const nextKey = numericKeys.length > 0 ? Math.max(...numericKeys) + 1 : 0;
        state.content = {
          ...state.content,
          [nextKey]: cleanElement,
        };
      }
    } else {
      state.content = { '0': cleanElement };
    }
  },

  setStatus(state, status) {
    state.status = status;
  },

  setLegalNotice(state, legalNotice) {
    state.legalNotice = legalNotice;
  },

  setInvisible(state, invisible) {
    state.invisible = invisible;
  },

  setOnLine(state, onLine) {
    state.onLine = !onLine;
  },

  setOnLineUpdated(state, onLineUpdated) {
    state.onLineUpdated = onLineUpdated;
  },
};

const actions = {
  updateSite({ commit }, site) {
    commit('setSite', site);
  },

  updateTemplate({ commit }, template) {
    commit('setTemplate', template);
  },

  updateSocialNetworks({ commit, state }, socialNetwork) {
    const { type, url } = socialNetwork;
    const social = {
      type,
      url,
    };

    if (!url) {
      commit('removeSocialNetwork', social);
      return;
    }

    if (state.socialNetworks.some(s => s.type === type)) {
      commit('modifySocialNetwork', social);
      return;
    }

    commit('addSocialNetwork', social);
  },

  updateDescription({ commit }, description) {
    commit('setDescription', description);
  },

  updateFees({ commit }, fees) {
    commit('setFees', fees);
  },

  updateColorConfig({ commit }, colorConfig) {
    commit('setColorConfig', colorConfig);
  },

  updateFontConfig({ commit }, fontConfig) {
    commit('setFontConfig', fontConfig);
  },

  updateAllFields({ commit }, siteVersion) {
    commit('setFontConfig', siteVersion.fontConfig);
    commit('setColorConfig', siteVersion.colorConfig);
    commit('setFees', siteVersion.fees);
    commit('setDescription', siteVersion.description);
    commit('setSite', siteVersion.site);
    commit('setTemplate', siteVersion.template['@id']);
    commit('setContent', siteVersion.content);
    commit('setStatus', siteVersion.status);
    commit('setLegalNotice', siteVersion.legalNotice);
    commit('setInvisible', siteVersion.invisible);
    commit('setOnLine', siteVersion.OnLine);
    commit('setOnLineUpdated', !siteVersion.OnLine);

    commit(
      'setSocialNetworks',
      siteVersion.socialNetworks.map(social => {
        const { type, url } = social;

        return {
          type,
          url,
        };
      })
    );
  },

  updateAllFieldsFirstly({ commit }, siteVersion) {
    commit('setFontConfig', siteVersion.fontConfig);
    commit('setColorConfig', siteVersion.colorConfig);
    commit('setFees', siteVersion.fees);
    commit('setDescription', siteVersion.description);
    commit('setSite', siteVersion.site);
    commit('setTemplate', siteVersion.template['@id']);
    commit('setContent', siteVersion.content);
    commit('setStatus', siteVersion.status);
    commit('setLegalNotice', siteVersion.legalNotice);
    commit('setInvisible', siteVersion.invisible);
    commit('setOnLine', !siteVersion.onLine);
    commit('setOnLineUpdated', siteVersion.onLine);

    commit(
      'setSocialNetworks',
      siteVersion.socialNetworks.map(social => {
        const { type, url } = social;
        return {
          type,
          url,
        };
      })
    );
  },

  updateContentElementById({ commit }, element) {
    commit('setContentElementById', element);
  },

  updateStatus({ commit }, status) {
    commit('setStatus', status);
  },

  updateLegalNotice({ commit }, legalNotice) {
    commit('setLegalNotice', legalNotice);
  },

  updateInvisible({ commit }, invisible) {
    commit('setInvisible', invisible);
  },

  updateOnLine({ commit }, onLine) {
    commit('setOnLine', onLine);
  },

  updateOnLineUpdated({ commit }, onLine) {
    commit('setOnLineUpdated', onLine);
  },
};

export default {
  namespaced: true,
  state: initialState(),
  getters,
  mutations,
  actions,
};
